// src/pages/attendance/DirectAttendanceModal.jsx
import React, { useState } from "react";
import { FiX, FiClock, FiMapPin } from "react-icons/fi";

const DirectAttendanceModal = ({ employee, type, date, onClose, onSubmit, loading }) => {
  // Get current time in HH:MM format for the input
  const getCurrentTimeForInput = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const [timeInput, setTimeInput] = useState(getCurrentTimeForInput());
  const [location, setLocation] = useState({ 
    latitude: "22.47977010", 
    longitude: "88.38777540" 
  });

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude.toFixed(8),
            longitude: position.coords.longitude.toFixed(8)
          });
        },
        (error) => {
          console.error("Error getting location:", error);
          alert("Unable to get current location. Using default coordinates.");
        }
      );
    } else {
      alert("Geolocation is not supported by this browser.");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // FIXED: Properly format time as HH:MM:SS (no extra :00)
    // First, clean the time input to ensure no double formatting
    let cleanTime = timeInput;
    
    // Remove any existing :00 at the end (in case of double formatting)
    if (cleanTime.endsWith(':00')) {
      cleanTime = cleanTime.slice(0, -3);
    }
    
    // Remove any extra colons or seconds
    const timeParts = cleanTime.split(':');
    let hours = timeParts[0] || "00";
    let minutes = timeParts[1] || "00";
    
    // Ensure hours and minutes are 2 digits
    hours = hours.padStart(2, '0').slice(0, 2);
    minutes = minutes.padStart(2, '0').slice(0, 2);
    
    // Format as HH:MM:SS
    const formattedTime = `${hours}:${minutes}:00`;
    
    // Prepare data based on check-in or check-out with coordinates as strings
    const submissionData = {
      user_id: employee.id,
      date: date,
      ...(type === 'checkin' 
        ? { 
            check_in: formattedTime,
            check_in_latitude: location.latitude,
            check_in_longitude: location.longitude
          }
        : { 
            check_out: formattedTime,
            check_out_latitude: location.latitude,
            check_out_longitude: location.longitude
          }
      )
    };
    
    onSubmit(submissionData);
  };

  const handleLatitudeChange = (e) => {
    const value = e.target.value;
    setLocation(prev => ({ 
      ...prev, 
      latitude: value 
    }));
  };

  const handleLongitudeChange = (e) => {
    const value = e.target.value;
    setLocation(prev => ({ 
      ...prev, 
      longitude: value 
    }));
  };

  const isCheckIn = type === 'checkin';

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {isCheckIn ? 'Direct Check In' : 'Direct Check Out'}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {employee.name} • {employee.designation}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all"
          >
            <FiX size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Date Display */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
            <label className="text-xs font-medium text-gray-500 uppercase">Date</label>
            <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">
              {new Date(date).toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>

          {/* Employee Info Summary */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
            <div className="text-xs text-blue-600 dark:text-blue-400 font-medium">Employee ID</div>
            <div className="text-sm text-gray-900 dark:text-white">{employee.id}</div>
          </div>

          {/* Time Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <FiClock className="inline mr-2" />
              {isCheckIn ? 'Check In Time' : 'Check Out Time'}
            </label>
            <input
              type="time"
              value={timeInput}
              onChange={(e) => setTimeInput(e.target.value)}
              step="1"
              className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white"
              required
            />
            <p className="text-xs text-gray-500 mt-1">Time in 24-hour format (HH:MM)</p>
          </div>

          {/* Location */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                <FiMapPin className="inline mr-2" />
                Location Coordinates
              </label>
              <button
                type="button"
                onClick={getCurrentLocation}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium"
              >
                Get Current Location
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-gray-500">Latitude</label>
                <input
                  type="text"
                  value={location.latitude}
                  onChange={handleLatitudeChange}
                  placeholder="22.47977010"
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white"
                  required
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">Longitude</label>
                <input
                  type="text"
                  value={location.longitude}
                  onChange={handleLongitudeChange}
                  placeholder="88.38777540"
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white"
                  required
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`flex-1 px-4 py-2.5 text-white rounded-lg transition-all font-medium flex items-center justify-center gap-2 ${
                isCheckIn 
                  ? 'bg-green-600 hover:bg-green-700' 
                  : 'bg-orange-600 hover:bg-orange-700'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  {isCheckIn ? 'Confirm Check In' : 'Confirm Check Out'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DirectAttendanceModal;